/**
 * Small parts of the Strategy deck (Figma 13, round 5): the view switch, the
 * status pill, a slide drawn at any width, and a thumbnail that only draws its
 * slide once it is near the viewport.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { LayoutGrid, RectangleHorizontal } from "lucide-react";
import { cn } from "@/utils/helpers";
import "../../components/kit/segmentedLens.css";

export type DeckView = "slides" | "grid";

/**
 * Strategy / View switch (34:60194, 34:60201): two icons on the control-glass
 * track, the lens travelling between them on snappy. Accessible names Slide
 * view and Grid view.
 */
export function DeckViewSwitch({ view, onChange }: { view: DeckView; onChange: (view: DeckView) => void }) {
  const options: { value: DeckView; label: string; Icon: typeof LayoutGrid }[] = [
    { value: "slides", label: "Slide view", Icon: RectangleHorizontal },
    { value: "grid", label: "Grid view", Icon: LayoutGrid },
  ];
  const index = view === "grid" ? 1 : 0;
  return (
    <div
      role="tablist"
      aria-label="View"
      className="co-glass-control relative inline-flex h-10 flex-none items-stretch rounded-pill p-[3px]"
      onKeyDown={(event) => {
        if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
        event.preventDefault();
        const next = options[(index + 1) % 2];
        onChange(next.value);
        requestAnimationFrame(() => document.getElementById(`sd-view-${next.value}`)?.focus());
      }}
    >
      <span
        aria-hidden="true"
        className="co-seg-lens pointer-events-none absolute bottom-[3px] left-0 top-[3px]"
        style={{ width: 38, transform: `translateX(${3 + index * 38}px)` }}
      />
      {options.map(({ value, label, Icon }) => (
        <button
          key={value}
          id={`sd-view-${value}`}
          type="button"
          role="tab"
          aria-label={label}
          title={label}
          aria-selected={view === value}
          tabIndex={view === value ? 0 : -1}
          onClick={() => onChange(value)}
          className={cn(
            "relative z-[1] grid w-[38px] place-items-center rounded-pill outline-none transition-colors duration-[var(--duration-fast)]",
            "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent",
            view === value ? "text-co-text" : "text-co-muted hover:text-co-text",
          )}
        >
          <Icon aria-hidden className="size-4" strokeWidth={1.75} />
        </button>
      ))}
    </div>
  );
}

/** Strategy / Status (34:59250): a pill, never a square (8 Oct). */
export function DeckStatus({ children }: { children: ReactNode }) {
  return <span className="sd-status">{children}</span>;
}

/** A 1280 × 720 slide drawn at `width`, without reflowing it (the slide CSS zooms). */
export function MiniSlide({ width, children, className }: { width: number; children: ReactNode; className?: string }) {
  return (
    <div
      className={cn("sd-mini", className)}
      style={{ "--strategy-slide-scale": width / 1280, inlineSize: width, blockSize: (width * 9) / 16 } as CSSProperties}
    >
      {children}
    </div>
  );
}

/**
 * A thumbnail: the real slide, inert, drawn only once it comes near the screen
 * (a deck is 40 to 70 slides, each a full composition).
 */
export function LazyMiniSlide({ width, render }: { width: number; render: () => ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const node = ref.current;
    if (!node || near) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [near]);
  return (
    <div
      ref={ref}
      className="sd-mini"
      inert
      aria-hidden="true"
      style={{ "--strategy-slide-scale": width / 1280, inlineSize: width, blockSize: (width * 9) / 16 } as CSSProperties}
    >
      {near ? render() : null}
    </div>
  );
}

/**
 * Measures an element's content box (the stage, so the slide fits it). A
 * callback ref, so an element that mounts later (switching views) is measured.
 */
export function useBox<T extends HTMLElement>(): [(node: T | null) => void, { width: number; height: number }] {
  const [node, setNode] = useState<T | null>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!node) return;
    const measure = () =>
      setBox((previous) =>
        previous.width === node.clientWidth && previous.height === node.clientHeight
          ? previous
          : { width: node.clientWidth, height: node.clientHeight },
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, box];
}

/** Slide · Being written (34:60147): the slide's final shape, in skeletons. */
export function SlideBeingWritten({ chapter, title }: { chapter: string; title: string }) {
  return (
    <div className="sd-being-written" role="status" aria-label={`Creo is writing ${chapter}`}>
      <p>{chapter}</p>
      <strong>{title}</strong>
      <div aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>
      <span aria-hidden="true" style={{ inlineSize: 380 }} />
      <span aria-hidden="true" style={{ inlineSize: 300 }} />
    </div>
  );
}

/** Loading: the filmstrip's chapter rows and the slide, in skeletons (no Figma frame; built from the parts). */
export function StrategyLoading() {
  return (
    <div className="sd-workspace" role="status" aria-label="Loading the strategy">
      <div className="sd-film-wrap">
        <div className="sd-film sd-skeleton" aria-hidden>
          <div className="sd-film-head"><i className="h-3 w-12 animate-pulse rounded-tag" /></div>
          <div className="sd-film-list">
            <i className="ms-[29px] mt-1 h-[88px] w-[156px] animate-pulse" style={{ borderRadius: 7 }} />
            {Array.from({ length: 8 }, (_, i) => <i key={i} className="ms-2 mt-4 h-3 animate-pulse rounded-tag" style={{ inlineSize: 120 + ((i * 37) % 50) }} />)}
          </div>
        </div>
      </div>
      <div className="sd-main sd-skeleton" aria-hidden>
        <div className="sd-toolbar"><i className="h-10 w-[84px] animate-pulse" style={{ borderRadius: 999 }} /><i className="h-6 w-64 animate-pulse rounded-tag" /></div>
        <div className="sd-stage"><i className="aspect-video w-full max-w-[700px] animate-pulse" style={{ borderRadius: 8 }} /></div>
      </div>
    </div>
  );
}
